/**
 * The screens. Three of them: the drawer, the viewfinder, and an item.
 *
 * The drawer is the home screen, not the camera. Discovery was unambiguous that
 * the pain is retrieval rather than clutter, and that a web app cannot win the
 * race against a lock-screen camera shortcut — so the app opens on the thing it
 * is actually better at than the phone's own camera: finding something again.
 */

import { mayDecrypt } from '@mayfly/core';
import { el, mount, ring, remaining, lifeLeft, bitmapUrl } from './ui.ts';
import { listTypes, getType, consequenceOf, expiryFor, type TypeDef } from './presets.ts';
import { showSettings } from './settings.ts';
import { storageStatus } from './storage-status.ts';
import { consent, setConsent } from './analytics.ts';
import { Camera } from './capture.ts';
import { getClock } from './clock.ts';
import * as store from './store.ts';
import type { LocalItem } from './store.ts';

let camera: Camera | null = null;

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The plus on the capture button. */
function plusIcon(): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'plus');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of ['M12 5v14', 'M5 12h14']) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    path.setAttribute('stroke-width', '2.4');
    path.setAttribute('stroke-linecap', 'round');
    svg.append(path);
  }
  return svg;
}

/** A quiet mark for the empty drawer, in place of a sentence. */
function markGlyph(): SVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 512 512');
  svg.setAttribute('class', 'mark-glyph');
  const ring = document.createElementNS(SVG_NS, 'path');
  ring.setAttribute('d', 'M370.4 119.6 A178 178 0 1 1 141.6 119.6');
  ring.setAttribute('fill', 'none');
  ring.setAttribute('stroke-width', '30');
  ring.setAttribute('stroke-linecap', 'round');
  svg.append(ring);
  for (const [cx, rot, op] of [[219, -13, '1'], [293, 13, '0.6']] as const) {
    const wing = document.createElementNS(SVG_NS, 'ellipse');
    wing.setAttribute('cx', String(cx));
    wing.setAttribute('cy', '238');
    wing.setAttribute('rx', '31'); wing.setAttribute('ry', '90');
    wing.setAttribute('transform', `rotate(${rot} ${cx} 238)`);
    wing.setAttribute('opacity', op);
    svg.append(wing);
  }
  return svg;
}

/** Remembered between renders so filtering does not reset as the drawer redraws. */
let activeType: string | null = null;

export async function showDrawer(filter = ''): Promise<void> {
  const clock = await getClock();
  const now = clock.now();
  await store.sweepExpired(now);

  const types = await listTypes();
  const labelOf = (id: string) => types.find((t) => t.id === id)?.label ?? id;

  const all = await store.listItems();
  const q = filter.trim().toLowerCase();
  let items = q
    ? all.filter((i) => `${i.label} ${labelOf(i.preset)}`.toLowerCase().includes(q))
    : all;
  if (activeType) items = items.filter((i) => i.preset === activeType);

  // Nearest the end first. What is about to go is what needs attention, and a
  // photo you are about to lose should not be buried under one that lives for a
  // month. Kept photos have no deadline, so they sit at the bottom.
  items = [...items].sort((a, b) => {
    if (a.expiresAt === null && b.expiresAt === null) return b.createdAt - a.createdAt;
    if (a.expiresAt === null) return 1;
    if (b.expiresAt === null) return -1;
    return a.expiresAt - b.expiresAt;
  });
  const untagged = all.filter((i) => !i.tagged).length;
  const storage = await storageStatus();
  const noticeDismissed = await store.meta<boolean>('storageNoticeSeen');
  const analytics = await consent();
  const soon = all.filter((i) =>
    i.expiresAt !== null && i.expiresAt - now < 3_600_000 && i.expiresAt > now);

  mount(
    el('header', { class: 'bar' }, [
      el('h1', { class: 'brand', text: 'mayfly' }),
      el('input', {
        class: 'search', type: 'search', value: filter,
        placeholder: 'Search your drawer', 'aria-label': 'Search your drawer',
        oninput: (e: Event) => void showDrawer((e.target as HTMLInputElement).value),
      }),
      el('button', {
        class: 'icon-btn', 'aria-label': 'Settings', title: 'Settings',
        text: '\u2699', onclick: () => void showSettings(),
      }),
    ]),
    typeFilter(types, all, filter),
    // Asked once, answered either way, and never nagged again. Ignoring it is a
    // valid answer: until someone says yes, the tag is loaded but denied, so it
    // stores nothing and sets no identifier.
    analytics === 'unset'
      ? el('div', { class: 'consent' }, [
          el('p', { class: 'consent-head', text: 'Can we count visits?' }),
          el('p', { class: 'consent-body', text:
            'Anonymous usage statistics through Google Analytics — which pages get opened, and '
            + 'what kind of device. Never your photos, their labels or their types: those stay on '
            + 'this device and are not readable by us or anyone else. Say no and Google is still '
            + 'contacted on each visit, but stores nothing and cannot identify you.' }),
          el('div', { class: 'consent-actions' }, [
            el('button', {
              class: 'primary small', text: 'Yes, count visits',
              onclick: async () => { await setConsent('granted'); await showDrawer(filter); },
            }),
            el('button', {
              class: 'ghost small', text: 'No thanks',
              onclick: async () => { await setConsent('denied'); await showDrawer(filter); },
            }),
          ]),
        ])
      : el('div'),
    // Shown whenever the browser has not promised to keep this data. That covers
    // a private window without having to guess whether we are in one.
    !storage.persisted && !noticeDismissed
      ? el('div', { class: 'notice' }, [
          el('p', {
            class: 'notice-head',
            text: 'These photos live only in this browser, and it has not promised to keep them.',
          }),
          el('p', {
            class: 'notice-body',
            text: 'In a private window they go when you close it. Otherwise your browser may '
              + 'clear them if it runs short of space. Adding Mayfly to your home screen makes '
              + 'that far less likely.',
          }),
          el('div', { class: 'notice-actions' }, [
            el('button', { class: 'ghost small', text: 'Storage settings', onclick: () => void showSettings() }),
            el('button', {
              class: 'ghost small', text: 'Got it',
              onclick: async () => { await store.setMeta('storageNoticeSeen', true); await showDrawer(filter); },
            }),
          ]),
        ])
      : el('div'),
    untagged > 0
      ? el('button', {
          class: 'tray', onclick: () => void showDrawer(''),
          text: `${untagged} waiting to be labelled — tag them whenever suits`,
        })
      : el('div'),
    soon.length > 0
      ? el('div', { class: 'soon' }, [
          el('p', {
            class: 'soon-head',
            text: soon.length === 1
              ? 'One photo goes within the hour.'
              : `${soon.length} photos go within the hour.`,
          }),
          el('ul', { class: 'soon-list' }, await Promise.all(soon.map(async (i) =>
            el('li', {}, [
              el('span', { text: i.label }),
              el('span', { class: 'left', text: remaining(i.expiresAt, now) }),
              el('button', { class: 'ghost small', text: 'Keep', onclick: () => void keep(i) }),
              el('button', { class: 'ghost small', text: '+1 day', onclick: () => void extend(i, 86_400_000) }),
            ])))),
        ])
      : el('div'),
    items.length === 0
      // No sentence when the drawer is simply empty — the plus says what to do.
      // A search that found nothing does need a word, or it looks broken.
      ? (q
          ? el('p', { class: 'empty', text: 'Nothing matches that.' })
          : el('div', { class: 'empty-mark', 'aria-hidden': 'true' }, [markGlyph()]))
      : el('ul', { class: 'grid' }, await Promise.all(items.map((i) => card(i, now)))),
    // Bottom-centre, thumb-reachable with either hand, and a plain plus: the one
    // thing this screen exists to let you do.
    el('button', {
      class: 'shutter-fab', 'aria-label': 'Take a photo', onclick: () => void showCapture(),
    }, [plusIcon()]),
  );
}

/** Filter chips, showing only types that actually have something filed under them. */
function typeFilter(types: TypeDef[], all: LocalItem[], filter: string): HTMLElement {
  const counts = new Map<string, number>();
  for (const i of all) counts.set(i.preset, (counts.get(i.preset) ?? 0) + 1);
  const present = types.filter((t) => counts.has(t.id));
  if (present.length < 2) return el('div');

  const chip = (id: string | null, label: string, count?: number) =>
    el('button', {
      class: activeType === id ? 'fchip on' : 'fchip',
      'aria-pressed': activeType === id,
      onclick: () => { activeType = id; void showDrawer(filter); },
      text: count === undefined ? label : `${label} ${count}`,
    });

  return el('div', { class: 'filters', role: 'group', 'aria-label': 'Filter by type' }, [
    chip(null, 'All'),
    ...present.map((t) => chip(t.id, t.label, counts.get(t.id))),
  ]);
}

async function keep(item: LocalItem): Promise<void> {
  await store.updateItem({ ...item, preset: 'keep', expiresAt: null, tagged: true });
  await showDrawer();
}

/** Extending is bounded: nothing becomes permanent by repeatedly nudging it. */
async function extend(item: LocalItem, byMs: number): Promise<void> {
  const clock = await getClock();
  const from = Math.max(item.expiresAt ?? clock.now(), clock.now());
  await store.updateItem({ ...item, expiresAt: from + byMs });
  await showDrawer();
}

async function card(item: LocalItem, now: number): Promise<HTMLElement> {
  const left = remaining(item.expiresAt, now);
  const preview = await store.revealPreview(item).catch(() => null);
  const type = await getType(item.preset);
  return el('li', { class: 'card', 'data-type': item.preset, onclick: () => void showItem(item.itemId) }, [
    preview
      ? el('img', { class: 'thumb', alt: '', src: bitmapUrl(preview) })
      : el('div', { class: 'thumb', 'data-preset': item.preset }),
    el('div', { class: 'meta' }, [
      el('span', { class: 'label', text: item.label || type?.label || item.preset }),
      el('span', { class: 'left', text: left }),
    ]),
    ring(lifeLeft(item.createdAt, item.expiresAt, now), item.expiresAt, left),
  ]);
}

/* --------------------------------------------------------------- viewfinder */

export async function showCapture(): Promise<void> {
  const video = el('video', { class: 'viewfinder', playsinline: true, autoplay: true, muted: true });
  const status = el('p', { class: 'status', text: 'Starting the camera…' });

  // A toggle, not a one-way switch — meter cupboards are dark and shop windows
  // are not. Devices without a torch say so rather than failing silently.
  let torchOn = false;
  const torchButton = el('button', {
    class: 'ghost', text: 'Torch off', 'aria-pressed': 'false',
    onclick: async () => {
      const wanted = !torchOn;
      const worked = await camera?.torch(wanted);
      if (!worked) { status.textContent = 'This camera has no torch.'; return; }
      torchOn = wanted;
      torchButton.textContent = torchOn ? 'Torch on' : 'Torch off';
      torchButton.setAttribute('aria-pressed', String(torchOn));
      torchButton.classList.toggle('on', torchOn);
    },
  });

  mount(
    el('div', { class: 'capture' }, [
      video, status,
      el('div', { class: 'capture-bar' }, [
        el('button', {
          class: 'ghost', text: 'Cancel',
          onclick: () => { camera?.stop(); camera = null; void showDrawer(); },
        }),
        el('button', { class: 'shutter', 'aria-label': 'Shutter', onclick: () => void take() }),
        torchButton,
      ]),
    ]),
  );

  camera = new Camera();
  try {
    await camera.start(video);
    status.textContent = 'Tap the shutter. You can label it afterwards.';
  } catch {
    status.textContent = 'Mayfly needs the camera to take a photo. Nothing is uploaded.';
  }

  async function take(): Promise<void> {
    const shot = await camera!.shoot();
    if (torchOn) await camera!.torch(false);
    camera!.stop();
    // Saved immediately as "just for now": the shutter never waits on a decision.
    const clock = await getClock();
    const offset = -new Date().getTimezoneOffset();
    const item = await store.saveCapture(shot.bytes, {
      preset: 'just-for-now',
      expiresAt: await expiryFor('just-for-now', clock.now(), offset),
      width: shot.width, height: shot.height, tagged: false, preview: shot.preview,
    });
    await showTagSheet(item);
  }
}

/* ---------------------------------------------------------------- tag sheet */

export async function showTagSheet(item: LocalItem): Promise<void> {
  const clock = await getClock();
  const offset = -new Date().getTimezoneOffset();
  const types = await listTypes();

  const choose = async (preset: string) => {
    const next: LocalItem = {
      ...item,
      preset,
      tagged: true,
      expiresAt: await expiryFor(preset, clock.now(), offset),
    };
    await store.updateItem(next);
    await showDrawer();
  };

  mount(
    el('div', { class: 'sheet-wrap' }, [
      el('div', { class: 'sheet' }, [
        el('h2', { text: 'What is it?' }),
        // Dismissing is a valid answer: the photo already has 24 hours and a nudge.
        el('p', { class: 'hint', text: 'Skip if you like — it keeps for 24 hours either way.' }),
        el('ul', { class: 'presets' }, types.map((p) =>
          el('li', {}, [
            el('button', {
              class: 'preset', 'data-preset': p.id, onclick: () => void choose(p.id),
            }, [
              el('span', { class: 'preset-label', text: p.label }),
              // Every preset states what it just decided. "If it just says Parking
              // and goes quiet, I don't know what I've agreed to."
              el('span', { class: 'preset-consequence', text: consequenceOf(p) }),
            ]),
          ])),
        ),
        el('button', { class: 'ghost wide', text: 'Skip for now', onclick: () => void showDrawer() }),
      ]),
    ]),
  );
}

/** Hands the photo to the operating system so the user can keep it properly. */
async function save(item: LocalItem): Promise<void> {
  const bytes = await store.reveal(item);
  const url = bitmapUrl(bytes);
  const a = el('a', { href: url, download: `mayfly-${item.itemId}.jpg` });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/* --------------------------------------------------------------------- item */

export async function showItem(itemId: string): Promise<void> {
  const item = await store.getItem(itemId);
  if (!item) return void showDrawer();
  const clock = await getClock();
  const gate = mayDecrypt(item.expiresAt, clock);

  const body = gate.allowed
    ? el('img', { class: 'full', alt: 'Your photo', src: bitmapUrl(await store.reveal(item)) })
    : el('p', {
        class: 'gone',
        text: gate.reason === 'clock-tampered'
          ? "Your device clock has moved backwards, so Mayfly can't tell what has expired."
          : 'Gone. This photo has expired.',
      });

  const left = remaining(item.expiresAt, clock.now());
  mount(
    el('header', { class: 'bar' }, [
      el('button', { class: 'ghost', text: 'Back', onclick: () => void showDrawer() }),
      el('span', { class: 'left', text: left }),
    ]),
    body,
    el('label', { class: 'field' }, [
      el('span', { text: 'Label — this is what search looks at' }),
      el('input', {
        class: 'search', type: 'text', value: item.label,
        placeholder: 'e.g. level 3 bay C', maxlength: 80,
        onchange: (e: Event) =>
          void store.updateItem({ ...item, label: (e.target as HTMLInputElement).value.slice(0, 80) }),
      }),
    ]),
    el('div', { class: 'actions' }, [
      item.expiresAt === null
        ? el('span')
        : el('button', { class: 'ghost', text: 'Keep', onclick: () => void keep(item) }),
      item.expiresAt === null
        ? el('span')
        : el('button', { class: 'ghost', text: '+1 day', onclick: () => void extend(item, 86_400_000) }),
      el('button', { class: 'ghost', text: 'Change type', onclick: () => void showTagSheet(item) }),
    ]),
    el('div', { class: 'actions' }, [
      // The honest escape hatch: a photo can only reach the camera roll if the user
      // saves it out deliberately. Mayfly cannot write there itself.
      gate.allowed
        ? el('button', { class: 'ghost', text: 'Save to Photos', onclick: () => void save(item) })
        : el('span'),
      el('button', {
        class: 'ghost danger', text: 'Delete now',
        onclick: async () => { await store.destroy(itemId); await showDrawer(); },
      }),
    ]),
  );
}
